import React from 'react';
import styled from 'styled-components';

/**
 * VendorIcon — a small brand-colored tile for an AI model vendor.
 *
 * ponytail: brand-colored monogram tiles, not hand-copied SVG logos. Reliable,
 * self-contained (CSP-safe, no npm dep), and reads as intentional. To swap in real
 * logos later, add an inline SVG to GLYPHS keyed by the normalized vendor and it
 * renders in place of the monogram — everything else stays the same.
 */

const BRAND = {
  anthropic: '#D97757', claude: '#D97757',
  openai: '#0F9D82', gpt: '#0F9D82',
  google: '#4285F4', gemini: '#4285F4', gemma: '#4285F4',
  meta: '#0866FF', metallama: '#0866FF', llama: '#0866FF',
  mistral: '#FA5210', mistralai: '#FA5210',
  deepseek: '#4D6BFE',
  xai: '#111827', grok: '#111827',
  qwen: '#615CED', alibaba: '#615CED',
  zai: '#2F6BFF', glm: '#2F6BFF',
  cohere: '#39594D',
  amazon: '#FF9900', aws: '#FF9900',
  perplexity: '#20808D',
  microsoft: '#0078D4',
  nvidia: '#76B900',
};

const GLYPHS = {}; // e.g. GLYPHS.openai = <svg ...>; wins over the monogram when the vendor matches

const FALLBACK = ['#6366F1', '#0EA5E9', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6'];

const normalize = (v) => (v || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const hashColor = (s) => FALLBACK[[...s].reduce((a, c) => a + c.charCodeAt(0), 0) % FALLBACK.length];

export function vendorColor(vendor) {
  const key = normalize(vendor);
  const hit = Object.keys(BRAND).find((k) => key.includes(k));
  return hit ? BRAND[hit] : hashColor(key || 'x');
}

const Tile = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: ${(p) => Math.round(p.$size * 0.28)}px;
  background: ${(p) => p.$color};
  color: #fff;
  font-size: ${(p) => Math.round(p.$size * 0.5)}px;
  font-weight: 700;
  line-height: 1;
  user-select: none;
`;

const VendorIcon = ({ vendor, size = 20 }) => {
  const key = normalize(vendor);
  const glyphKey = Object.keys(GLYPHS).find((k) => key.includes(k));
  const label = vendor || 'model';
  const monogram = vendor ? vendor.trim().charAt(0).toUpperCase() : '•';
  return (
    <Tile $size={size} $color={vendorColor(vendor)} title={label} aria-label={label} role="img">
      {glyphKey ? GLYPHS[glyphKey] : monogram}
    </Tile>
  );
};

export default VendorIcon;
